import type { SettingGroup } from './settingsCatalog.js';

/**
 * SIGN-IN AND SECURITY — the settings group, extracted.
 *
 * `settingsCatalog.ts` was long past its file budget, and this is the group
 * whose members each need a paragraph: a session timeout that silently
 * overrides the refresh TTL, a device gate that queues the entire company on
 * the day it is switched on, and now a company-level two-factor switch whose
 * "off" must not be mistaken for "erase everybody's enrolment".
 *
 * The type import is type-only, so the cycle back to `settingsCatalog.ts`
 * exists at compile time and not at runtime.
 */
export const SECURITY_GROUP: SettingGroup = {
  id: 'security',
  custom: 'kioskLock',
  sections: [
    {
      id: 'security.passwords',
      fields: [
        {
          id: 'security.minLength',
          category: 'security',
          path: 'passwordMinLength',
          kind: 'number',
          min: 6,
          max: 64,
          step: 1,
        },
        {
          id: 'security.uppercase',
          category: 'security',
          path: 'passwordRequireUppercase',
          kind: 'toggle',
        },
        {
          id: 'security.number',
          category: 'security',
          path: 'passwordRequireNumber',
          kind: 'toggle',
        },
        {
          id: 'security.special',
          category: 'security',
          path: 'passwordRequireSpecial',
          kind: 'toggle',
        },
      ],
    },
    {
      // TWO-FACTOR, AND WHY IT IS IN SETTINGS AT ALL.
      //
      // Enrolling is personal and lives on the profile page, which is right:
      // only the person holding the phone can scan the QR. But whether the
      // company OFFERS the factor is not personal, and an admin who wants it
      // gone had nowhere to say so — they came here, found four password
      // rules, and left. A feature with a per-person on switch and no
      // company-level off switch reads as a feature that cannot be turned
      // off.
      //
      // A `select` rather than a toggle for the same reason
      // `deviceApprovalMode` is one: the modes name what the gate DOES, and
      // "required for everyone" is the obvious third member — a toggle would
      // have to be replaced to grow one.
      id: 'security.twoFactor',
      fields: [
        {
          id: 'security.twoFactorMode',
          category: 'security',
          path: 'twoFactorMode',
          kind: 'select',
          options: [
            { value: 'optional', labelKey: 'settings.opt.twoFactor.optional' },
            { value: 'off', labelKey: 'settings.opt.twoFactor.off' },
          ],
        },
      ],
    },
    {
      id: 'security.sessions',
      fields: [
        {
          id: 'security.sessionTimeout',
          category: 'security',
          path: 'sessionTimeoutMinutes',
          kind: 'number',
          min: 5,
          // Matches SecuritySettingsSchema's ceiling (one week). The control
          // must not be tighter than the schema, or the highest value a
          // tenant is allowed to store is one they cannot type in.
          max: 10080,
          step: 5,
          unitKey: 'settings.unit.minutes',
        },
        {
          id: 'security.maxAttempts',
          category: 'security',
          path: 'maxFailedLoginAttempts',
          kind: 'number',
          min: 3,
          max: 20,
          step: 1,
        },
        {
          id: 'security.lockout',
          category: 'security',
          path: 'lockoutDurationMinutes',
          kind: 'number',
          min: 1,
          max: 720,
          step: 5,
          unitKey: 'settings.unit.minutes',
        },
        {
          id: 'security.pinLength',
          category: 'security',
          path: 'pinLength',
          kind: 'number',
          min: 4,
          max: 8,
          step: 1,
        },
        {
          // The switch that arms the device approval queue. It is a `select`
          // rather than a toggle because "off" and "block sign-in" are not
          // opposite states of one thing — the mode names what the gate DOES,
          // and a future mode (flag without blocking, say) has somewhere to
          // go. `deviceApprovalExemptRoles` is deliberately not exposed here:
          // it is a break-glass list, and a role picker beside the switch
          // invites emptying it on the same visit that turns the gate on.
          id: 'security.deviceApproval',
          category: 'security',
          path: 'deviceApprovalMode',
          kind: 'select',
          options: [
            { value: 'off', labelKey: 'settings.opt.deviceApproval.off' },
            { value: 'gate_login', labelKey: 'settings.opt.deviceApproval.gateLogin' },
          ],
        },
      ],
    },
  ],
};
